import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logoutUser();
    navigate('/');
    setMenuOpen(false);
  };

  const scrollTo = (id) => {
    setMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Home', action: () => scrollTo('home') },
    { label: 'About Us', action: () => scrollTo('about') },
    { label: 'Tire Finder', action: () => scrollTo('finder') },
    { label: 'Products', action: () => scrollTo('products') },
    { label: 'Services', action: () => scrollTo('services') },
    { label: 'Contact', action: () => scrollTo('contact') },
  ];

  // 🔥 shared animation handlers
  const handleHoverIn = (e) => {
    e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
    e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.25)';
    e.currentTarget.style.color = '#fff';
  };

  const handleHoverOut = (e) => {
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.background = 'transparent';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
  };

  const handlePress = (e) => {
    e.currentTarget.style.transform = 'scale(0.96)';
  };

  const handleRelease = (e) => {
    e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
  };

  return (
    <nav
      style={{
        ...styles.nav,
        boxShadow: scrolled ? '0 6px 20px rgba(0,0,0,0.25)' : 'none'
      }}
    >
      <div style={styles.container}>

        {/* Logo */}
        <Link to="/" style={styles.logoWrapper} onClick={() => setMenuOpen(false)}>
          <span style={styles.logoText}>𖣐 Empire1Tires</span>
          <span style={styles.logoSlogan}>We beat any deal 🤝</span>
        </Link>

        {/* Desktop Links */}
        <div className="nav-desktop-links" style={styles.desktopLinks}>
          {navLinks.map((link, i) => (
            <button
              key={i}
              style={styles.navLink}
              onClick={link.action}
              onMouseEnter={handleHoverIn}
              onMouseLeave={handleHoverOut}
              onMouseDown={handlePress}
              onMouseUp={handleRelease}
            >
              {link.label}
            </button>
          ))}

          <Link
            to="/products"
            style={styles.shopLink}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.color = '#0b2545';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Shop All
          </Link>
        </div>

        {/* Right Side */}
        <div style={styles.rightSide}>
          <div className="nav-desktop-auth" style={styles.desktopAuth}>
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin" style={styles.adminBtn}>Admin</Link>
                )}
                <Link to="/my-account" style={styles.myAccountBtn}>
                  👤 {user.name.split(' ')[0]}
                </Link>
                <button style={styles.logoutBtn} onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" style={styles.loginBtn}>Login</Link>
                <Link to="/register" style={styles.registerBtn}>Register</Link>
              </>
            )}
          </div>

          <button
            className="nav-hamburger"
            style={styles.hamburger}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="nav-mobile-menu" style={styles.mobileMenu}>
          {navLinks.map((link, i) => (
            <button
              key={i}
              style={styles.mobileLink}
              onClick={link.action}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {link.label}
            </button>
          ))}

          <Link to="/products" style={styles.mobileLinkA} onClick={() => setMenuOpen(false)}>
            🛍️ Shop All Products
          </Link>

          <div style={styles.mobileDivider} />

          {user ? (
            <>
              <Link to="/my-account" style={styles.mobileLinkA} onClick={() => setMenuOpen(false)}>👤 My Account</Link>
              <button style={styles.mobileLogout} onClick={handleLogout}>
                🚪 Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" style={styles.mobileLinkA} onClick={() => setMenuOpen(false)}>🔐 Login</Link>
              <Link to="/register" style={styles.mobileRegisterBtn} onClick={() => setMenuOpen(false)}>✨ Register</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

const styles = {
  nav: {
    background: '#0b2545',
    position: 'sticky',
    top: 0,
    zIndex: 1000
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '70px',
    overflow: 'hidden'
  },
  logoWrapper: {
    display: 'flex',
    flexDirection: 'column',
    textDecoration: 'none',
    flexShrink: 0
  },
  logoText: {
    color: '#fff',
    fontSize: '20px',
    fontWeight: '700'
  },
  logoSlogan: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '11px'
  },
  desktopLinks: {
    display: 'flex',
    gap: '4px',
    flexShrink: 1,
    overflow: 'hidden'
  },
  navLink: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.85)',
    padding: '6px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
    whiteSpace: 'nowrap'
  },
  shopLink: {
    border: '1px solid #fff',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '6px',
    textDecoration: 'none',
    transition: 'all 0.25s ease',
    fontSize: '14px',
    whiteSpace: 'nowrap'
  },
  rightSide: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0
  },
  cartBtn: { color: '#fff', fontSize: '20px', position: 'relative', textDecoration: 'none' },
  cartBadge: {
    position: 'absolute',
    top: '-5px',
    right: '-5px',
    background: '#ff4d4f',
    color: '#fff',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  desktopAuth: { display: 'flex', gap: '8px', alignItems: 'center' },
  loginBtn: { color: '#fff', textDecoration: 'none', fontSize: '14px', whiteSpace: 'nowrap' },
  registerBtn: { background: '#fff', color: '#0b2545', padding: '6px 10px', borderRadius: '6px', textDecoration: 'none', fontSize: '14px', whiteSpace: 'nowrap' },
  myAccountBtn: { color: '#fff', textDecoration: 'none', fontSize: '14px', whiteSpace: 'nowrap' },
  adminBtn: { color: '#ffd166', textDecoration: 'none', fontSize: '14px' },
  logoutBtn: { background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '14px' },
  hamburger: {
    display: 'none',
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '22px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1
  },

  mobileMenu: {
    background: '#0b2545',
    padding: '12px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    borderTop: '1px solid rgba(255,255,255,0.1)'
  },
  mobileLink: {
    color: '#fff',
    background: 'none',
    border: 'none',
    padding: '12px 0',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '16px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    transition: 'transform 0.15s'
  },
  mobileLinkA: {
    color: '#fff',
    textDecoration: 'none',
    display: 'block',
    padding: '12px 0',
    fontSize: '16px',
    borderBottom: '1px solid rgba(255,255,255,0.06)'
  },
  mobileDivider: { height: '1px', background: 'rgba(255,255,255,0.2)', margin: '8px 0' },
  mobileLogout: { color: '#ff4d4f', background: 'none', border: 'none', padding: '12px 0', textAlign: 'left', fontSize: '16px', cursor: 'pointer' },
  mobileRegisterBtn: {
    background: '#c41e3a',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: '8px',
    textDecoration: 'none',
    display: 'block',
    textAlign: 'center',
    fontWeight: '600',
    marginTop: '8px'
  }
};

export default Navbar;